import type { CreateOrgUserRequest, PortalCharity } from '@afra/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, type FormEvent, useState } from 'react'
import { RequestError, api } from '../../lib/api'
import { ROLE_FA, faNum } from './labels'

/**
 * Every charity Afra has on record, not only the ones with an account.
 *
 * This is the list people expect when they say "the charities": about two
 * thousand of them, from the portal extract. Only a few have signed in here,
 * and a few hundred already have belief computed from their past evaluations —
 * both of which this list says outright, because the difference is the whole
 * state of the rollout.
 *
 * Nothing is created by looking. An organization comes into existence when an
 * account is made for one of these rows, and its ledger backfills in the same
 * breath.
 */
export function PortalTab({ canManage }: { canManage: boolean }) {
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [provisioning, setProvisioning] = useState<number | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()

  const portal = useQuery({
    queryKey: ['admin-portal', search, page],
    queryFn: () => api.adminPortalCharities({ search: search.trim() || undefined, page }),
  })

  const create = useMutation({
    mutationFn: (input: CreateOrgUserRequest) => api.adminCreateUser(input),
    onSuccess: (result) => {
      setError(null)
      setProvisioning(null)
      const parts = [`حساب ${result.user.email} ساخته شد`]
      if (result.backfilled > 0) parts.push(`${faNum(result.backfilled)} مورد از خودارزیابی قبلی‌اش آورده شد`)
      if (result.factsWritten > 0) parts.push(`${faNum(result.factsWritten)} مورد به شناسنامه‌اش اضافه شد`)
      setNotice(`${parts.join(' و ')}.`)
      queryClient.invalidateQueries({ queryKey: ['admin-portal'] })
      queryClient.invalidateQueries({ queryKey: ['admin-orgs'] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'حساب ساخته نشد.'),
  })

  const data = portal.data
  const lastPage = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1
  const from = data ? (data.page - 1) * data.pageSize + 1 : 0
  const to = data ? Math.min(data.page * data.pageSize, data.total) : 0

  const onSearch = (value: string) => {
    setSearch(value)
    setPage(1)
  }

  return (
    <div className="stack">
      {error && <p className="notice notice--error">{error}</p>}
      {notice && <p className="notice notice--teach">{notice}</p>}

      <div className="card">
        <div className="field">
          <label htmlFor="portal-search">جست‌وجو در فهرست افرا</label>
          <input
            id="portal-search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="بخشی از نام مؤسسه"
          />
          {data && (
            <p className="meta">
              <span className="num">{faNum(data.total)}</span> مؤسسه در فهرست افرا. آن‌هایی که
              «حساب دارد» نخورده‌اند، هنوز هیچ‌کس از طرفشان وارد سامانه نشده است.
            </p>
          )}
        </div>

        {portal.isLoading && <p className="muted">در حال بارگذاری…</p>}
        {data && data.charities.length === 0 && (
          <p className="muted">هیچ مؤسسه‌ای با این نام پیدا نشد.</p>
        )}

        {data && data.charities.length > 0 && (
          <>
            <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>مؤسسه</th>
                    <th>شهر</th>
                    <th>ارزیابی‌ها</th>
                    <th>دانسته از ارزیابی‌ها</th>
                    <th>وضعیت</th>
                    {canManage && <th />}
                  </tr>
                </thead>
                <tbody>
                  {data.charities.map((charity) => (
                    <Fragment key={charity.id}>
                      <tr>
                        <td>
                          <div>{charity.title}</div>
                          <div className="meta">شمارهٔ پرتال {faNum(charity.id)}</div>
                        </td>
                        <td>{charity.city ?? '—'}</td>
                        <td>
                          <span className="num">{faNum(charity.evaluations)}</span>
                        </td>
                        <td>
                          {charity.knownFromLegacy > 0 ? (
                            <span>
                              <span className="num">{faNum(charity.knownFromLegacy)}</span> از{' '}
                              <span className="num">{faNum(185)}</span>
                            </span>
                          ) : (
                            <span className="muted">چیزی محاسبه نشده</span>
                          )}
                        </td>
                        <td>
                          {charity.orgId === null ? (
                            <span className="badge badge--stored">بدون حساب</span>
                          ) : (
                            <span className="badge badge--good">حساب دارد</span>
                          )}
                          {charity.hasCertificate && (
                            <div className="meta">گواهی افرا دارد</div>
                          )}
                        </td>
                        {canManage && (
                          <td>
                            <div className="table__actions">
                              {charity.orgId === null && (
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--sm"
                                  onClick={() =>
                                    setProvisioning(provisioning === charity.id ? null : charity.id)
                                  }
                                >
                                  ساختن حساب
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>

                      {provisioning === charity.id && (
                        <tr>
                          <td colSpan={6}>
                            <ProvisionForm
                              charity={charity}
                              pending={create.isPending}
                              onCancel={() => setProvisioning(null)}
                              onSubmit={(input) => create.mutate(input)}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="row" style={{ justifyContent: 'space-between' }}>
              <span className="meta">
                <span className="num">{faNum(from)}</span> تا{' '}
                <span className="num">{faNum(to)}</span> از{' '}
                <span className="num">{faNum(data.total)}</span>
              </span>
              <span className="row">
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  قبلی
                </button>
                <button
                  type="button"
                  className="btn btn--quiet btn--sm"
                  disabled={page >= lastPage}
                  onClick={() => setPage((p) => p + 1)}
                >
                  بعدی
                </button>
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

/**
 * Making the account is what brings the organization into existence, so this
 * says so — and asks whether the charity's past in the portal comes with it.
 * The admin decides; both can be brought in later from the organization list.
 */
function ProvisionForm({
  charity,
  pending,
  onCancel,
  onSubmit,
}: {
  charity: PortalCharity
  pending: boolean
  onCancel: () => void
  onSubmit: (input: CreateOrgUserRequest) => void
}) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [role, setRole] = useState<'member' | 'org_admin'>('org_admin')
  const [importAssessment, setImportAssessment] = useState(charity.evaluations > 0)
  const [importProfile, setImportProfile] = useState(true)

  const submit = (event: FormEvent) => {
    event.preventDefault()
    onSubmit({
      email,
      password,
      displayName: displayName.trim() || undefined,
      role,
      mustChangePassword: true,
      legacyCharityId: charity.id,
      orgId: null,
      orgName: null,
      importAssessment,
      importProfile,
    })
  }

  return (
    <form className="notice notice--info" onSubmit={submit}>
      <p>با ساختن این حساب، «{charity.title}» به سامانه اضافه می‌شود.</p>

      <label className="check check--plain">
        <input
          type="checkbox"
          checked={importAssessment}
          disabled={charity.evaluations === 0}
          onChange={(e) => setImportAssessment(e.target.checked)}
        />
        <span>جواب‌های خودارزیابی قبلی را بیاور</span>
        <span className="check__hint">
          {charity.evaluations > 0 ? (
            <>
              از <span className="num">{faNum(charity.evaluations)}</span> ارزیابی ثبت‌شده. به‌عنوان «تأییدنشده»
              می‌آیند و افرا در گفت‌وگو از همان‌جا شروع می‌کند، نه از صفر.
            </>
          ) : (
            'این مؤسسه ارزیابی قبلی ندارد.'
          )}
        </span>
      </label>
      <label className="check check--plain">
        <input type="checkbox" checked={importProfile} onChange={(e) => setImportProfile(e.target.checked)} />
        <span>اطلاعات شناسنامه را بیاور</span>
        <span className="check__hint">
          شمار کارکنان و داوطلبان، درآمد سالانه، شهر، مأموریت و فعالیت‌ها، هر کدام که در پرتال ثبت شده باشد.
        </span>
      </label>

      <div className="grid-2">
        <div className="field">
          <label htmlFor={`portal-email-${charity.id}`}>ایمیل</label>
          <input
            id={`portal-email-${charity.id}`}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`portal-password-${charity.id}`}>گذرواژه</label>
          <input
            id={`portal-password-${charity.id}`}
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="دست‌کم ۱۲ نویسه"
          />
        </div>
        <div className="field">
          <label htmlFor={`portal-name-${charity.id}`}>نام صاحب حساب</label>
          <input
            id={`portal-name-${charity.id}`}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
          />
        </div>
        <div className="field">
          <label htmlFor={`portal-role-${charity.id}`}>نقش در مؤسسه</label>
          <select
            id={`portal-role-${charity.id}`}
            value={role}
            onChange={(e) => setRole(e.target.value as 'member' | 'org_admin')}
          >
            <option value="org_admin">{ROLE_FA.org_admin}</option>
            <option value="member">{ROLE_FA.member}</option>
          </select>
        </div>
      </div>

      <div className="row">
        <button
          type="submit"
          className="btn btn--sm"
          disabled={pending || !email.includes('@') || password.length < 12}
        >
          {pending ? 'در حال ساختن…' : 'حساب را بساز'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel}>
          بی‌خیال
        </button>
      </div>
    </form>
  )
}
